import type { ListingType } from "@/types/property";

const ONE_BILLION = 1_000_000_000;
const ONE_MILLION = 1_000_000;

/**
 * Format a VND price into a human-friendly Vietnamese string.
 *
 * - SALE prices: prefer "tỷ" when >= 1 billion, otherwise "triệu".
 * - RENT prices: prefer "triệu" with "/tháng" suffix; luxury rentals
 *   in the billions still render as "tỷ/tháng".
 * - Values are rounded to one decimal place; a trailing ".0" is stripped.
 *
 * Examples:
 *   formatPrice(3_200_000_000, "SALE") → "3.2 tỷ"
 *   formatPrice(18_500_000,    "SALE") → "18.5 triệu"
 *   formatPrice(25_000_000,    "RENT") → "25 triệu/tháng"
 *   formatPrice(1_500_000_000, "RENT") → "1.5 tỷ/tháng"
 */
export function formatPrice(value: number, listingType: ListingType): string {
  const suffix = listingType === "RENT" ? "/tháng" : "";

  if (value >= ONE_BILLION) {
    return `${trim(value / ONE_BILLION)} tỷ${suffix}`;
  }
  if (value >= ONE_MILLION) {
    return `${trim(value / ONE_MILLION)} triệu${suffix}`;
  }
  // Fall back to a grouped VND figure for sub-million values.
  return `${value.toLocaleString("vi-VN")} đ${suffix}`;
}

function trim(value: number): string {
  const rounded = Math.round(value * 10) / 10;
  const fixed = rounded.toFixed(1);
  return fixed.endsWith(".0") ? fixed.slice(0, -2) : fixed;
}
